/** Legacy OPS / UI codes → catalog action codes (keep in sync with BE PermissionMatch). */
const LEGACY_PERMISSION_ALIASES: Record<string, string> = {
  "create:warehouse_location": "CargoGo Wms#create:storage_location",
  "get:warehouse_location": "CargoGo Wms#get:storage_location",
  "get_list:warehouse_location": "CargoGo Wms#get_list:storage_location",
  "update:warehouse_location": "CargoGo Wms#update:storage_location",
  "delete:warehouse_location": "CargoGo Wms#delete:storage_location",
};

export function normalizePermissionCode(code: string | undefined | null): string {
  if (!code) {
    return "";
  }
  const trimmed = code.trim();
  return LEGACY_PERMISSION_ALIASES[trimmed] ?? trimmed;
}

/** Coerce JWT / API permission claims into a string[]. */
export function coercePermissionList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((p): p is string => typeof p === "string" && p.length > 0)
      .map((p) => normalizePermissionCode(p))
      .filter(Boolean);
  }
  if (typeof value === "string" && value.length > 0) {
    const n = normalizePermissionCode(value);
    return n ? [n] : [];
  }
  return [];
}

export function coerceModuleList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((m): m is string => typeof m === "string" && m.trim().length > 0)
      .map((m) => m.trim());
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return [value.trim()];
  }
  return [];
}

/**
 * Menu / route gate — **modules only** (Role.modules / JWT `modules` claim).
 * Does not treat action codes (`Module#…`) as enough to show a module.
 */
export function hasModuleAccess(
  modules: string[] | undefined | null,
  moduleKey: string | undefined | null,
): boolean {
  if (!modules?.length || !moduleKey) {
    return false;
  }
  const key = moduleKey.trim();
  if (!key) {
    return false;
  }
  return modules.some((m) => m === key);
}

/**
 * @deprecated Prefer {@link hasModuleAccess} with JWT/API modules.
 * Kept for action-code matching where needed.
 */
export function hasModulePermission(
  permissions: string[] | undefined | null,
  moduleKey: string | undefined | null,
): boolean {
  if (!permissions?.length || !moduleKey) {
    return false;
  }
  const key = moduleKey.trim();
  if (!key) {
    return false;
  }
  return permissions.some((raw) => {
    const p = normalizePermissionCode(raw);
    return p === key || p.startsWith(`${key}#`);
  });
}

/** Count action-like codes only (exclude bare module names present in catalog). */
export function countActionPermissions(
  permissions: string[] | undefined | null,
  catalogModules: Iterable<string> | undefined | null,
): number {
  if (!permissions?.length) {
    return 0;
  }
  const modules = new Set(catalogModules ?? []);
  return permissions.filter((raw) => {
    const p = normalizePermissionCode(raw);
    return p.includes("#") || !modules.has(p);
  }).length;
}
